// googleMapsInit.ts
// Simple Google Maps initialization to be imported in App.tsx

import { GOOGLE_MAPS_CONFIG } from './config/maps';


let mapsInitialized = false;
let mapsInitializationPromise: Promise<boolean> | null = null;

/**
 * Initialize Google Maps API globally
 * Call this function once at the app level (App.tsx)
 */
export function initGoogleMaps(): Promise<boolean> {
  console.log('🌎 [GoogleMaps] Initializing Google Maps API');
  
  // If already initialized or in progress, return the existing promise
  if (mapsInitialized) {
    console.log('✅ [GoogleMaps] Already initialized');
    return Promise.resolve(true);
  }
  
  if (mapsInitializationPromise) {
    console.log('⏳ [GoogleMaps] Initialization already in progress');
    return mapsInitializationPromise;
  }
  
  // If Google Maps is already loaded via script tag
  if (window.google && window.google.maps && window.google.maps.places) {
    console.log('✅ [GoogleMaps] API already loaded via script tag');
    mapsInitialized = true;
    return Promise.resolve(true);
  }
  
  // Create the initialization promise
  mapsInitializationPromise = new Promise<boolean>((resolve) => {
    console.log('⏳ [GoogleMaps] Starting initialization');
    
    // Check if the script already exists
    const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
    if (existingScript) {
      console.log('⚠️ [GoogleMaps] Script tag already exists, waiting for it to load');
      waitForMapsToLoad().then(success => {
        mapsInitialized = success;
        resolve(success);
      });
      return;
    }
    
    // Create script element
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${getGoogleMapsApiKey()}&libraries=places`;
    script.async = true;
    script.defer = true;
    
    // Handle script loading success
    script.onload = () => {
      console.log('✅ [GoogleMaps] Script loaded successfully');
      waitForMapsToLoad().then(success => {
        mapsInitialized = success;
        resolve(success);
      });
    };
    
    // Handle script loading failure
    script.onerror = () => {
      console.error('❌ [GoogleMaps] Failed to load script');
      mapsInitializationPromise = null;
      resolve(false);
    };
    
    // Add script to document
    document.head.appendChild(script);
    console.log('🔄 [GoogleMaps] Script tag added to document head');
  });
  
  return mapsInitializationPromise;
}

/**
 * Wait for Google Maps to be fully loaded
 */
function waitForMapsToLoad(timeout = 10000): Promise<boolean> {
  return new Promise((resolve) => {
    // If already available, resolve immediately
    if (window.google && window.google.maps && window.google.maps.places) {
      console.log('✅ [GoogleMaps] Maps API immediately available');
      resolve(true);
      return;
    }
    
    console.log('⏳ [GoogleMaps] Waiting for Maps API to be available...');
    
    // Set timeout
    const timeoutId = setTimeout(() => {
      console.error('⌛ [GoogleMaps] Timed out waiting for Maps API');
      if (checkInterval) clearInterval(checkInterval);
      resolve(false);
    }, timeout);
    
    // Check regularly
    const checkInterval = setInterval(() => {
      if (window.google && window.google.maps && window.google.maps.places) {
        clearTimeout(timeoutId);
        clearInterval(checkInterval);
        console.log('✅ [GoogleMaps] Maps API now available');
        resolve(true);
      }
    }, 200);
  });
}

/**
 * Get the Google Maps API key from environment variables or config
 */
function getGoogleMapsApiKey(): string {
  // Get from environment variable
/*   const apiKey = import.meta.env?.GOOGLE_MAPS_CONFIG || 
                 process.env?.REACT_APP_GOOGLE_MAPS_API_KEY || 
                 process.env?.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY ||
                 window.__GOOGLE_MAPS_API_KEY__; */
  const apiKey = GOOGLE_MAPS_CONFIG.apiKey               
                 
  if (!apiKey) {
    console.warn('⚠️ [GoogleMaps] No API key found in environment variables');
  }
  
  return apiKey || '';
}

/**
 * Check if Google Maps API is ready to use
 */
export function isGoogleMapsReady(): boolean {
  return mapsInitialized && !!window.google?.maps?.places;
}

// Type definition for global variables
declare global {
  interface Window {
    google?: any;
    __GOOGLE_MAPS_API_KEY__?: string;
  }
}